import React from "react";
import styled from "styled-components";

import type { AcceptPosition } from "../../app/services/dmThreads";
import {
  formatOfferDateTime,
  formatOfferAmountWithTax,
  formatOfferAcceptPositionInvite,
  formatOfferRoleScope,
} from "../../utils/offerFormatters";

/**
 * オファーの要点サマリ表示部品。
 * - 送信前の最終確認モーダル（OfferConfirmModal）
 * - 受信者向けの受領モーダル（OfferReceiveModal）
 * - 既存スレッドの「オファー詳細はこちら」再表示
 * の三箇所で同じ見た目で再利用する。
 *
 * 表示項目（仕様書 dm-threads.md §5 / §3）:
 * - 受信者名 / アバター（任意）
 * - 担当ロール（assigned_roles）
 * - スコープ（scope）
 * - 納期（deadline）
 * - 対価（contract_amount）+ 着手金率（advance_payment_rate）
 * - 受諾時付与ポジション（accept_position）
 * - 成果物の提出有無（requires_deliverable）
 * - 再委託許可（allow_subcontracting）
 * - メッセージ本文（initial_message）
 *
 * 値が `undefined` / `null` のフィールドは「未設定」と表示する。
 */
export interface OfferSummaryCardData {
  recipientLabel?: string | null;
  recipientAvatarUrl?: string | null;
  assignedRoles?: string | null;
  scope?: string | null;
  deadline?: string | null;
  contractAmount?: number | null;
  /** 着手金率 0–100。表示は `xx%` */
  advancePaymentRate?: number | null;
  /** 受諾時付与ポジション。`PRODUCER` / `DIRECTOR` / `SPECIALIST` / `null`（指定なし） */
  acceptPosition?: AcceptPosition | null;
  requiresDeliverable?: boolean | null;
  allowSubcontracting?: boolean | null;
  initialMessage?: string | null;
}

interface OfferSummaryCardProps {
  data: OfferSummaryCardData;
  /** 受信者表示行を出すかどうか（送信側モーダルのみ true 推奨） */
  showRecipient?: boolean;
}

// ===== スタイル =====

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  background: var(--soremo-background);
`;

const Title = styled.div.attrs({ className: "heading-spacing-13" })`
  color: var(--soremo-text);
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const FieldLabel = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const FieldValue = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
  white-space: pre-wrap;
  word-break: break-word;
`;

const RecipientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--soremo-border);
`;

const RecipientName = styled.div.attrs({ className: "heading-spacing-13" })`
  color: var(--soremo-text);
`;

const RecipientAvatar = styled.div<{ $src?: string | null }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $src }) =>
    $src ? `url(${$src}) center/cover` : "var(--soremo-border)"};
  flex-shrink: 0;
`;

const Empty = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

// ラベルを持たない一文の注記（招聘ポジション・再委託可否など）。
const NoteLine = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
  white-space: pre-wrap;
  word-break: break-word;
`;

// ===== ヘルパー =====

const renderValue = (value: string | null | undefined) =>
  value && value.length > 0 ? value : <Empty>未設定</Empty>;

// ===== 本体 =====

const OfferSummaryCard: React.FC<OfferSummaryCardProps> = ({
  data,
  showRecipient = true,
}) => {
  const roleScopeText = formatOfferRoleScope(data.assignedRoles, data.scope);
  const deadlineText = formatOfferDateTime(data.deadline);
  const amountText = formatOfferAmountWithTax(data.contractAmount);
  const acceptPositionInvite = formatOfferAcceptPositionInvite(
    data.acceptPosition,
  );

  return (
    <Card>
      {showRecipient && (
        <RecipientRow>
          <RecipientAvatar $src={data.recipientAvatarUrl ?? undefined} />
          <RecipientName>
            {data.recipientLabel ?? <Empty>受信者未選択</Empty>}
          </RecipientName>
        </RecipientRow>
      )}

      <Title>契約条件</Title>

      <Field>
        <FieldLabel>ロール</FieldLabel>
        <FieldValue>{renderValue(roleScopeText)}</FieldValue>
      </Field>

      <Field>
        <FieldLabel>納期</FieldLabel>
        <FieldValue>{renderValue(deadlineText)}</FieldValue>
      </Field>

      <Field>
        <FieldLabel>対価</FieldLabel>
        <FieldValue>{renderValue(amountText)}</FieldValue>
      </Field>

      {data.advancePaymentRate !== undefined &&
        data.advancePaymentRate !== null && (
          <Field>
            <FieldLabel>着手金率</FieldLabel>
            <FieldValue>{`${data.advancePaymentRate}%`}</FieldValue>
          </Field>
        )}

      {/* 受諾時付与ポジションは「招聘」一文で表示（スペシャリスト・変更なしは非表示）。 */}
      {acceptPositionInvite && <NoteLine>{acceptPositionInvite}</NoteLine>}

      {/* 再委託は「可能」のときだけ明示する（不可は記載しない）。 */}
      {data.allowSubcontracting === true && (
        <NoteLine>再委託が可能です</NoteLine>
      )}

      {data.initialMessage !== undefined && (
        <Field>
          <FieldLabel>メッセージ</FieldLabel>
          <FieldValue>{renderValue(data.initialMessage)}</FieldValue>
        </Field>
      )}
    </Card>
  );
};

export default OfferSummaryCard;
